import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { useParams } from "react-router-dom";
import { get, put } from "../../../../utils/api";

// Types
interface Permissions {
  create: boolean;
  edit: boolean;
  list: boolean;
  delete: boolean;
  export: boolean;
}

interface Link {
  to: string;
  name: string;
  permissions: Permissions;
  enabled: boolean; // Added enabled state for each link
}

interface MenuSection {
  title: string;
  links: Link[];
  enabled: boolean;
}

// Styled Components omitted for brevity

// Styled components
const Container = styled.div`
  padding: 30px;
  background: #f5f8fb;
  color: #343a40;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  max-width: 900px;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
`;

const SectionContainer = styled.div<{ disabled: boolean }>`
  margin-bottom: 25px;
  background-color: #ffffff;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e0e6ed;
  box-shadow: ${(props) =>
    props.disabled ? "none" : "0 4px 12px rgba(0, 0, 0, 0.08)"};
  transition: all 0.3s ease;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  ${(props) =>
    props.disabled &&
    css`
      background-color: #f0f2f5;
    `}
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  color: #2b6cb0;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LinkContainer = styled.div`
  margin: 12px 0;
  padding: 12px 15px;
  background-color: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const LinkHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: bold;
  font-size: 16px;
  color: #2d3748;
`;

const LabelGroup = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const Label = styled.label`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;

  input {
    margin-right: 6px;
  }
`;

const ToggleButton = styled.button<{ enabled: boolean }>`
  padding: 8px 16px;
  background-color: ${(props) => (props.enabled ? "#38a169" : "#e53e3e")};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${(props) => (props.enabled ? "#2f855a" : "#c53030")};
  }
`;

const SaveButtonContainer = styled.div`
  text-align: right;
  margin-top: 30px;
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background-color: #2b6cb0;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s, box-shadow 0.3s;

  &:hover {
    background-color: #2c5282;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const MenuEditor: React.FC = () => {
  const [menuSections, setMenuSections] = useState<MenuSection[]>([]);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    fetchMenuAndPermissions();
  }, [id]);

  const fetchMenuAndPermissions = async () => {
    try {
      const menuResponse = await get("settings/details", { settings_id: 1 });
      const permissionsResponse = await get(
        `admin_user_roles/details?role_id=${id}`,
        {}
      );

      const menuData = menuResponse.data.side_menu;
      const permissionsData = permissionsResponse.data.permissions;

      const mergedSections = menuData.map((section: MenuSection) => {
        const permissionSection = permissionsData.find(
          (perm: any) => perm.title === section.title
        );
        return {
          ...section,
          enabled: permissionSection ? permissionSection.enabled : false,
          links: section.links.map((link) => {
            const permissionLink = permissionSection?.links.find(
              (permLink: any) => permLink.to === link.to
            );
            return {
              ...link,
              permissions: permissionLink
                ? permissionLink.permissions
                : {
                    create: false,
                    edit: false,
                    list: false,
                    delete: false,
                    export: false,
                  },
              enabled: permissionLink ? permissionLink.enabled : false, // Ensure the link's enabled state is handled
            };
          }),
        };
      });

      setMenuSections(mergedSections);
    } catch (error) {
      console.error("Error fetching menu or permissions: ", error);
    }
  };

  const togglePermission = (
    sectionIndex: number,
    linkIndex: number,
    permissionType: keyof Permissions
  ): void => {
    const updatedSections = menuSections.map((section, sIndex) => {
      if (sIndex === sectionIndex) {
        const updatedLinks = section.links.map((link, lIndex) => {
          if (lIndex === linkIndex) {
            return {
              ...link,
              permissions: {
                ...link.permissions,
                [permissionType]: !link.permissions[permissionType]
              }
            };
          }
          return link;
        });
        return { ...section, links: updatedLinks };
      }
      return section;
    });
    setMenuSections(updatedSections);
  };

  const toggleSection = (sectionIndex: number): void => {
    const updatedSections = menuSections.map((section, index) => {
      if (index === sectionIndex) {
        return { ...section, enabled: !section.enabled };
      }
      return section;
    });
    setMenuSections(updatedSections);
  };

  const toggleLinkEnabled = (sectionIndex: number, linkIndex: number): void => {
    const updatedSections = menuSections.map((section, sIndex) => {
      if (sIndex === sectionIndex) {
        const updatedLinks = section.links.map((link, lIndex) => {
          if (lIndex === linkIndex) {
            return { ...link, enabled: !link.enabled };
          }
          return link;
        });
        return { ...section, links: updatedLinks };
      }
      return section;
    });
    setMenuSections(updatedSections);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await put(`admin_user_roles/${id}`, { permissions: menuSections });
      console.log("Permissions saved successfully");
      window.location.reload();

    } catch (error) {
      console.error("Failed to save permissions:", error);
    }
  };

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        {menuSections.map((section, sectionIndex) => (
          <SectionContainer key={section.title} disabled={!section.enabled}>
            <SectionTitle>
              {section.title}
              <ToggleButton
                enabled={section.enabled}
                type="button"
                onClick={() => toggleSection(sectionIndex)}
              >
                {section.enabled ? "Enabled" : "Disabled"}
              </ToggleButton>
            </SectionTitle>
            {section.links.map((link, linkIndex) => (
              <LinkContainer
                key={link.to}
                style={{ opacity: link.enabled ? 1 : 0.6 }}
              >
                <LinkHeader>
                  {link.name}
                  <ToggleButton
                    enabled={link.enabled}
                    type="button"
                    onClick={() => toggleLinkEnabled(sectionIndex, linkIndex)}
                  >
                    {link.enabled ? "Enabled" : "Disabled"}
                  </ToggleButton>
                </LinkHeader>
                <LabelGroup>
                  {Object.keys(link.permissions).map((perm) => (
                    <Label key={perm}>
                      <input
                        type="checkbox"
                        checked={link.permissions[perm as keyof Permissions]}
                        disabled={!link.enabled}
                        onChange={() =>
                          togglePermission(
                            sectionIndex,
                            linkIndex,
                            perm as keyof Permissions
                          )
                        }
                      />
                      {perm.charAt(0).toUpperCase() + perm.slice(1)}
                    </Label>
                  ))}
                </LabelGroup>
              </LinkContainer>
            ))}
          </SectionContainer>
        ))}
        <SaveButtonContainer>
          <SaveButton type="submit">Save Changes</SaveButton>
        </SaveButtonContainer>
      </form>
    </Container>
  );
};

export default MenuEditor;
